/* === AgAutocomplete — Anagogia Design System === */

import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '../utils/usePortalContainer';
import css from './AgAutocomplete.module.css';

/** Minuscules sans marques diacritiques, pour comparer une saisie à un libellé. */
const foldForSearch = (text: string): string =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

interface AgAutocompleteProps<T> {
  /** Available options */
  options: T[];
  /** Extract display label from option */
  getOptionLabel: (option: T) => string;
  /** Currently selected value(s) */
  value?: T | T[] | null;
  /** Change handler */
  onChange?: (event: React.SyntheticEvent, value: T | T[] | null) => void;
  /** Enable multi-select */
  multiple?: boolean;
  /** Input label */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Helper text below input */
  helperText?: string;
  /** Error state */
  error?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Custom option renderer */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement> & { key: string }, option: T, state: { selected: boolean }) => React.ReactNode;
  /** Custom tag renderer for multi-select */
  renderTags?: (
    value: T[],
    getTagProps: (params: { index: number }) => { onDelete: () => void; key: string }
  ) => React.ReactNode;
  /** Custom renderer for the selected value in single-select mode (chip, badge...). Replaces the plain label written into the input. */
  renderValue?: (value: T, helpers: { clear: () => void }) => React.ReactNode;
  /** Margin */
  margin?: 'none' | 'normal';
  /** Additional class */
  className?: string;
  /** Filter options beyond text search */
  filterOptions?: (options: T[], state: { inputValue: string }) => T[];
  /** No options text */
  noOptionsText?: string;
  /** Allow arbitrary text input (not limited to options) */
  freeSolo?: boolean;
  /** Controlled input value */
  inputValue?: string;
  /** Called when input text changes */
  onInputChange?: (event: React.SyntheticEvent | null, value: string) => void;
  /** Show loading indicator */
  loading?: boolean;
  /** Keep dropdown open after selection in multi-select */
  disableCloseOnSelect?: boolean;
  /** Custom equality check for options */
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  /** Show a chevron and turn the whole control into a click-to-toggle combobox (single-select vocabulary) */
  dropdownIndicator?: boolean;
  /** Show a clear button next to the selected value in single-select mode */
  clearable?: boolean;
  /** Accessible name of the clear button */
  clearLabel?: string;
  /** Accessible name of the field when no visible label is rendered */
  ariaLabel?: string;
  /** Additional class merged on the portal dropdown list (e.g. raise its z-index above a modal overlay) */
  dropdownClassName?: string;
}

/**
 * Champ de saisie à suggestions filtrées.
 * @category Formulaires
 */
function AgAutocomplete<T>({
  options,
  getOptionLabel,
  value,
  onChange,
  multiple = false,
  label,
  placeholder,
  helperText,
  error = false,
  disabled = false,
  renderOption,
  renderTags,
  renderValue,
  margin = 'none',
  className,
  filterOptions,
  noOptionsText = 'Aucun résultat',
  freeSolo = false,
  inputValue: controlledInputValue,
  onInputChange,
  loading = false,
  disableCloseOnSelect = false,
  isOptionEqualToValue,
  dropdownClassName,
  dropdownIndicator = false,
  clearable = false,
  clearLabel = 'Effacer la sélection',
  ariaLabel,
}: AgAutocompleteProps<T>) {
  const isInputControlled = controlledInputValue !== undefined;
  const [internalInputValue, setInternalInputValue] = useState('');
  const inputValue = isInputControlled ? controlledInputValue : internalInputValue;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Le focus rendu au champ après un choix ne doit pas rouvrir la liste qu'il vient de fermer. */
  const focusApresChoix = useRef(false);

  // En plein écran, seul le sous-arbre de l'élément plein écran est peint :
  // la liste s'y accroche pour rester visible (sinon `document.body`).
  const portalContainer = usePortalContainer(rootRef, isOpen);

  const updateInputValue = (val: string, event: React.SyntheticEvent | null = null) => {
    if (!isInputControlled) setInternalInputValue(val);
    onInputChange?.(event, val);
  };

  const optionEquals = useCallback(
    (a: T, b: T) => {
      if (isOptionEqualToValue) return isOptionEqualToValue(a, b);
      return getOptionLabel(a) === getOptionLabel(b);
    },
    [isOptionEqualToValue, getOptionLabel]
  );

  // Filter options (sans tenir compte de la casse ni des accents : « reflex » trouve « Réflexions »)
  const filtered = filterOptions
    ? filterOptions(options, { inputValue })
    : options.filter(opt => foldForSearch(getOptionLabel(opt)).includes(foldForSearch(inputValue)));

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        // Also check if click is inside the portal dropdown
        const dropdown = document.getElementById(dropdownId);
        if (dropdown && dropdown.contains(e.target as Node)) return;
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute dropdown position when open
  const inputId = useId();
  const dropdownId = `ag-autocomplete-dropdown-${useRef(Math.random().toString(36).slice(2)).current}`;
  useEffect(() => {
    if (!isOpen || !rootRef.current) {
      setDropdownPos(null);
      return;
    }
    const updatePos = () => {
      const rect = rootRef.current!.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [isOpen]);

  // A la fermeture sans selection, la recherche saisie est effacee pour rendre la main
  // a la valeur rendue (sinon elle resterait masquee par du texte mort).
  useEffect(() => {
    if (isOpen || multiple || !renderValue || isInputControlled) return;
    if (internalInputValue !== '') setInternalInputValue('');
  }, [isOpen, multiple, renderValue, isInputControlled, internalInputValue]);

  const isSelected = useCallback(
    (option: T) => {
      if (multiple && Array.isArray(value)) {
        return value.some(v => optionEquals(v, option));
      }
      if (!multiple && value) {
        return optionEquals(value as T, option);
      }
      return false;
    },
    [value, multiple, optionEquals]
  );

  const handleSelect = (option: T) => {
    if (multiple) {
      const currentValues = (value as T[]) || [];
      const alreadySelected = currentValues.some(v => optionEquals(v, option));
      const newValues = alreadySelected
        ? currentValues.filter(v => !optionEquals(v, option))
        : [...currentValues, option];
      onChange?.({ type: 'change' } as React.SyntheticEvent, newValues);
      updateInputValue('');
      if (!disableCloseOnSelect) {
        setIsOpen(false);
        focusApresChoix.current = true;
      }
    } else {
      onChange?.({ type: 'change' } as React.SyntheticEvent, option);
      updateInputValue(renderValue ? '' : getOptionLabel(option));
      setIsOpen(false);
      focusApresChoix.current = true;
    }
    inputRef.current?.focus();
    focusApresChoix.current = false;
  };

  const handleRemoveTag = (index: number) => {
    if (multiple && Array.isArray(value)) {
      const newValues = value.filter((_, i) => i !== index);
      onChange?.({ type: 'change' } as React.SyntheticEvent, newValues);
    }
  };

  const handleClearValue = () => {
    onChange?.({ type: 'change' } as React.SyntheticEvent, null);
    updateInputValue('');
  };

  const getTagProps = (params: { index: number }) => ({
    onDelete: () => handleRemoveTag(params.index),
    key: `tag-${params.index}`,
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => Math.min(prev + 1, filtered.length - 1));
      if (!isOpen) setIsOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    } else if (e.key === 'Enter' && freeSolo && inputValue) {
      e.preventDefault();
      onChange?.({ type: 'change' } as React.SyntheticEvent, inputValue as unknown as T);
      updateInputValue('');
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      // Liste ouverte : Échap ne ferme que la liste ; fermée, la frappe remonte à la fenêtre.
      if (isOpen) {
        e.stopPropagation();
        setIsOpen(false);
      }
    } else if (e.key === 'Backspace' && !inputValue && multiple && Array.isArray(value) && value.length > 0) {
      handleRemoveTag(value.length - 1);
    } else if (e.key === 'Backspace' && !inputValue && !multiple && renderValue && value) {
      handleClearValue();
    }
  };

  // Mode renderValue : la valeur selectionnee occupe le champ tant que l'utilisateur ne tape rien.
  const showRenderedValue = !multiple && !!renderValue && value != null && !inputValue;
  const showClearButton = clearable && !multiple && value != null && !disabled;

  // En combobox a chevron, c'est le clic qui ouvre et referme : le focus seul n'ouvre plus,
  // sinon le premier clic ouvrirait (focus) puis refermerait (toggle) dans la foulee.
  const handleControlClick = () => {
    inputRef.current?.focus();
    if (dropdownIndicator) setIsOpen(prev => !prev);
  };

  const rootCls = [
    css.root,
    margin === 'normal' ? css.marginNormal : undefined,
    className,
  ].filter(Boolean).join(' ');

  const wrapperCls = [
    css.inputWrapper,
    isOpen ? css.inputWrapperFocused : undefined,
    error ? css.inputWrapperError : undefined,
    showRenderedValue ? css.inputWrapperWithValue : undefined,
    dropdownIndicator ? css.inputWrapperSelect : undefined,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootCls} ref={rootRef}>
      {label && <label className={css.label} htmlFor={inputId}>{label}</label>}

      <div
        className={wrapperCls}
        data-testid="ag-autocomplete-control"
        onClick={handleControlClick}
      >
        {/* Render selected tags in multi-select mode */}
        {multiple && Array.isArray(value) && value.length > 0 && (
          renderTags
            ? renderTags(value, getTagProps)
            : value.map((v, index) => (
                <span key={`tag-${index}`} className={css.tag}>
                  {getOptionLabel(v)}
                  <span
                    className={css.tagDelete}
                    onClick={(e) => { e.stopPropagation(); handleRemoveTag(index); }}
                  >
                    ×
                  </span>
                </span>
              ))
        )}
        {showRenderedValue && (
          <span className={css.value}>
            {renderValue!(value as T, { clear: handleClearValue })}
          </span>
        )}
        <input
          ref={inputRef}
          id={inputId}
          className={css.input}
          value={inputValue}
          onChange={(e) => {
            updateInputValue(e.target.value, e);
            setIsOpen(true);
            setHighlightIndex(-1);
          }}
          onFocus={dropdownIndicator ? undefined : () => { if (!focusApresChoix.current) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={
            (multiple && Array.isArray(value) && value.length > 0) || showRenderedValue
              ? undefined
              : placeholder
          }
          disabled={disabled}
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-controls={dropdownId}
          aria-autocomplete="list"
        />

        {(showClearButton || dropdownIndicator) && (
          <span className={css.adornments}>
            {showClearButton && (
              <button
                type="button"
                className={css.clearButton}
                aria-label={clearLabel}
                title={clearLabel}
                onClick={(e) => { e.stopPropagation(); handleClearValue(); }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
            {dropdownIndicator && (
              <span
                className={[css.indicator, isOpen ? css.indicatorOpen : undefined].filter(Boolean).join(' ')}
                data-testid="ag-autocomplete-indicator"
                aria-hidden="true"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
          </span>
        )}
      </div>

      {helperText && <div className={css.helperText}>{helperText}</div>}

      {isOpen && !disabled && dropdownPos && createPortal(
        <ul
          id={dropdownId}
          className={[css.dropdown, dropdownClassName].filter(Boolean).join(' ')}
          role="listbox"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
        >
          {loading ? (
            <li className={css.noOptions}>Chargement...</li>
          ) : filtered.length === 0 ? (
            <li className={css.noOptions}>{noOptionsText}</li>
          ) : (
            filtered.map((option, index) => {
              const selected = isSelected(option);
              const optCls = [
                css.option,
                index === highlightIndex ? css.optionHighlighted : undefined,
                selected ? css.optionSelected : undefined,
              ].filter(Boolean).join(' ');

              if (renderOption) {
                return renderOption(
                  {
                    key: `opt-${index}`,
                    className: optCls,
                    onClick: () => handleSelect(option),
                    role: 'option',
                    'aria-selected': selected,
                  },
                  option,
                  { selected }
                );
              }

              return (
                <li
                  key={`opt-${index}`}
                  className={optCls}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={selected}
                >
                  {getOptionLabel(option)}
                </li>
              );
            })
          )}
        </ul>,
        portalContainer
      )}
    </div>
  );
}

export { AgAutocomplete };
export type { AgAutocompleteProps };
export default AgAutocomplete;
